import { useEffect, useState } from 'react';
import { axiosInstance } from 'utils/axios';

// material-ui
import { Box, Button, Grid, Typography, TextField, Select, MenuItem, Autocomplete, CircularProgress } from '@mui/material';

import { useTheme } from '@mui/material/styles';
import { deepPurple } from '@mui/material/colors';
import { gridSpacing } from 'app/constant';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectCurrentToken } from 'features/auth/authSlice';
import useFetchData from 'hooks/useFetchData';
import StickyHeadTable from './components/StickyHeadTable';
import InsertCustomerTasklistRecord from './components/InsertCustomerTasklistRecord';
import { warningAlert, successAlert, errorAlert } from 'ui-component/Toasts';

const CustomerTasklistRecord = () => {
    const user = useSelector(selectCurrentUser);
    const token = useSelector(selectCurrentToken);
    const theme = useTheme();

    const [customerTasklist, setCustomerTasklist] = useState([]);
    const [filterClientName, setFilterClientName] = useState('');
    const [filterClientLoginUserName, setFilterClientLoginUserName] = useState('');
    const [filterTaskName, setFilterTaskName] = useState('');
    const [filterStatusID, setFilterStatusID] = useState(0);
    const [filterCustomerID, setFilterCustomerID] = useState('');
    const [filterIsPackage, setFilterIsPackage] = useState('all');
    const { data: customerNameOptions, error: expensesTypesError } = useFetchData('/Customer/Names');
    const { data: customerLoginNamesOptions } = useFetchData('/Customer/LoginUserNames');
    const { data: taskNameOptions, error: taskNameError } = useFetchData('/TaskCodes');

    const [isLoading, setLoading] = useState(true);
    const [filterBtnDisabled, setFilterBtnDisabled] = useState(false);

    //fetch sales start
    const fetchCustomerTasklistRecord = async () => {
        let customerTasklistRecordURL = `/CustomerTasklist/All?`;
        if (filterTaskName !== '') {
            customerTasklistRecordURL += `&taskName=${filterTaskName}`;
        }

        if (filterClientName !== '') {
            customerTasklistRecordURL += `&clientName=${filterClientName}`;
        }

        if (filterClientLoginUserName !== '') {
            customerTasklistRecordURL += `&loginUserName=${filterClientLoginUserName}`;
        }

        if (filterStatusID !== 0) {
            customerTasklistRecordURL += `&statusID=${filterStatusID}`;
        }

        if (filterCustomerID !== '') {
            customerTasklistRecordURL += `&numberCode=${filterCustomerID}`;
        }

        if (filterIsPackage !== 'all') {
            if (filterIsPackage === 'true') {
                customerTasklistRecordURL += `&isPackage=true`;
            }
            if (filterIsPackage === 'false') {
                customerTasklistRecordURL += `&isPackage=false`;
            }
        }

        setLoading(true);
        setFilterBtnDisabled(true);
        try {
            const res = await axiosInstance.get(customerTasklistRecordURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setCustomerTasklist(res.data);
                console.log(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setFilterBtnDisabled(false);
            setLoading(false);
        }
    };

    const [customerTasklistStatusList, setCustomerTasklistStatusList] = useState([]);
    const fetchCustomerTasklistStatus = async () => {
        try {
            const res = await axiosInstance.get('/CustomerTasklistStatus/All', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setCustomerTasklistStatusList(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        }
    };

    // fetch task list
    const [tasklist, setTasklist] = useState([]);
    const fetchTaskList = async (filterTaskValueRangeStart = null, filterTaskValueRangeEnd = null) => {
        let params = '';
        if (filterTaskValueRangeStart >= 0 && filterTaskValueRangeEnd >= 0) {
            params = `?taskValueRangeStart=${filterTaskValueRangeStart}&taskValueRangeEnd=${filterTaskValueRangeEnd}`;
        }
        let taskListURL = '/Tasklist/All' + params;
        try {
            const res = await axiosInstance.get(taskListURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setTasklist(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        }
    };

    // With Promise All
    useEffect(() => {
        setLoading(true); // Set loading to true before starting the fetches

        // Define an array of async functions to fetch data
        const fetchFunctions = [fetchCustomerTasklistStatus()];

        // Use Promise.all to wait for all fetches to complete
        Promise.all(fetchFunctions)
            .then((values) => {
                setLoading(false); // Set loading to false after all fetches are done
            })
            .catch((error) => {
                // Handle any errors that occurred during fetching
                console.error('Error fetching data:', error);
                setLoading(false); // Set loading to false even if there was an error
            });
    }, []);

    return (
        <Grid container item xs={12} padding="20px">
            <InsertCustomerTasklistRecord
                tasklist={tasklist}
                fetchTaskList={fetchTaskList}
                customerNameOptions={customerNameOptions}
                customerLoginUsernamesOptions={customerLoginNamesOptions}
                taskNameOptions={taskNameOptions}
                customerTasklistStatusList={customerTasklistStatusList}
            />
            <Grid item container xs={12} spacing={gridSpacing} sx={{ marginBottom: '10px', overflowY: 'auto' }}>
                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Task Name :
                        </Typography>
                        <Autocomplete
                            freeSolo
                            value={filterTaskName}
                            onChange={(event, newValue) => {
                                setFilterTaskName(newValue);
                            }}
                            inputValue={filterTaskName}
                            onInputChange={(event, newInputValue) => {
                                setFilterTaskName(newInputValue);
                            }}
                            id="controllable-states-demo"
                            options={taskNameOptions}
                            sx={{ width: 170 }}
                            renderInput={(params) => <TextField {...params} />}
                            disableClearable
                        />
                    </Box>
                </Grid>
                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Login UserName :
                        </Typography>
                        <Autocomplete
                            freeSolo
                            value={filterClientLoginUserName}
                            onChange={(event, newValue) => {
                                setFilterClientLoginUserName(newValue);
                            }}
                            inputValue={filterClientLoginUserName}
                            onInputChange={(event, newInputValue) => {
                                setFilterClientLoginUserName(newInputValue);
                            }}
                            id="controllable-states-demo"
                            options={customerLoginNamesOptions}
                            sx={{ width: 170 }}
                            renderInput={(params) => <TextField {...params} />}
                            disableClearable
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Status :
                        </Typography>

                        <Select
                            displayEmpty
                            value={filterStatusID}
                            onChange={(e) => setFilterStatusID(e.target.value)}
                            // sx={{width:"100%"}}
                        >
                            <MenuItem sx={{ width: '100%' }} value="">
                                Select Status
                            </MenuItem>
                            {customerTasklistStatusList.map((item, index) => (
                                <MenuItem sx={{ width: '100%' }} value={item.id} key={index}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Customer ID :
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            value={filterCustomerID}
                            onChange={(e) => setFilterCustomerID(e.target.value)}
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Is Package :
                        </Typography>
                        <Select value={filterIsPackage} onChange={(e) => setFilterIsPackage(e.target.value)}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="true">True</MenuItem>
                            <MenuItem value="false">False</MenuItem>
                        </Select>
                    </Box>
                </Grid>

                <Grid item container xs={12} md={12} lg={12}>
                    <Button
                        variant="contained"
                        sx={{
                            float: 'right',
                            display: 'block',
                            mx: 'auto',
                            width: '150px',
                            backgroundColor: deepPurple[500],
                            marginTop: '20px',
                            marginBottom: '30px'
                        }}
                        onClick={() => fetchCustomerTasklistRecord()}
                        disabled={filterBtnDisabled}
                    >
                        Filter
                    </Button>
                </Grid>
                <Box sx={{ width: '100%', minHeight: '200px' }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <StickyHeadTable
                            customerTasklist={customerTasklist}
                            customerNameOptions={customerNameOptions}
                            taskNameOptions={taskNameOptions}
                            customerTasklistStatusList={customerTasklistStatusList}
                        />
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default CustomerTasklistRecord;
